import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Plus, Loader2, BookOpen, Eye, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './CSS/SeriesAdmin.module.css';
import { useDeleteSeries, useSeries } from '../../../hooks/admin/useAdminSeries';

const DeleteConfirmDialog = ({ isOpen, onClose, onConfirm, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Delete Series</h3>
          <p>Are you sure you want to delete this series? This action cannot be undone.</p>
        </div>
        <div className={styles.modalFooter}>
          <button 
            className={`${styles.button} ${styles.primaryButton}`}
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button 
            className={`${styles.button} ${styles.dangerButton}`}
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Loader2 className={styles.spinner} />
            ) : (
              <>
                <Trash2 size={18} />
                Delete
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const SeriesAdmin = () => {
  const navigate = useNavigate();
  const { series, isLoading, refetchSeries } = useSeries();
  const { deleteSeries, isLoading: isDeleting } = useDeleteSeries();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSeriesId, setSelectedSeriesId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const handleDelete = (id) => {
    setSelectedSeriesId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedSeriesId) {
      await deleteSeries(selectedSeriesId);
      setDeleteDialogOpen(false);
      refetchSeries();
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'published':
        return styles.published;
      case 'draft':
        return styles.draft;
      case 'archived':
        return styles.archived;
      default:
        return '';
    }
  };

  const filteredSeries = series?.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate pagination
  const totalPages = filteredSeries ? Math.ceil(filteredSeries.length / itemsPerPage) : 0;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSeries?.slice(indexOfFirstItem, indexOfLastItem);

  // Pagination handlers
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>
            <BookOpen size={28} />
            Series Management
          </h2>
          <div className={styles.searchContainer}>
            <Search size={18} className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search series..."
              className={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button 
            className={`${styles.button} ${styles.primaryButton}`}
            onClick={() => navigate('/admin/add-series')}
          >
            <Plus size={20} />
            Add New Series
          </button>
        </div>
        <div className={styles.cardContent}>
          {isLoading ? (
            <div className={styles.loading}>
              <Loader2 className={styles.spinner} />
              Loading series data...
            </div>
          ) : (
            <>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Language</th>
                      <th>Chapters</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems?.length > 0 ? (
                      currentItems.map((item) => (
                        <tr key={item.id}>
                          <td>{item.title}</td>
                          <td>{item.series_type}</td>
                          <td>
                            <span className={`${styles.statusBadge} ${getStatusBadgeClass(item.status)}`}>
                              {item.status}
                            </span>
                          </td>
                          <td>{item.language}</td>
                          <td>{item.total_chapters.toLocaleString()}</td>
                          <td>
                            <div className={styles.actionButtons}>
                              <button 
                                className={`${styles.button} ${styles.viewButton} ${styles.iconButton}`}
                                onClick={() => navigate(`/admin/view-series/${item.id}`)}
                                title="View Series"
                              >
                                <Eye size={18} />
                              </button>
                              <button 
                                className={`${styles.button} ${styles.primaryButton} ${styles.iconButton}`}
                                onClick={() => navigate(`/admin/edit-series/${item.id}`)}
                                title="Edit Series"
                              >
                                <Pencil size={18} />
                              </button>
                              <button 
                                className={`${styles.button} ${styles.dangerButton} ${styles.iconButton}`}
                                onClick={() => handleDelete(item.id)}
                                title="Delete Series"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className={styles.noResults}>
                          No series found. Try a different search term.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {filteredSeries?.length > 0 && (
                <div className={styles.pagination}>
                  <button 
                    className={`${styles.paginationButton} ${currentPage === 1 ? styles.disabled : ''}`}
                    onClick={prevPage}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  
                  <div className={styles.pageNumbers}>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(number => {
                        // Show first page, last page, current page and one page before and after current
                        return (
                          number === 1 || 
                          number === totalPages || 
                          (number >= currentPage - 1 && number <= currentPage + 1)
                        );
                      })
                      .map((number, index, array) => {
                        // Add ellipsis where needed
                        const showEllipsisBefore = index > 0 && array[index - 1] !== number - 1;
                        const showEllipsisAfter = index < array.length - 1 && array[index + 1] !== number + 1;
                        
                        return (
                          <React.Fragment key={number}>
                            {showEllipsisBefore && (
                              <span className={styles.ellipsis}>...</span>
                            )}
                            
                            <button 
                              className={`${styles.pageNumber} ${currentPage === number ? styles.activePage : ''}`}
                              onClick={() => goToPage(number)}
                            >
                              {number}
                            </button>
                            
                            {showEllipsisAfter && (
                              <span className={styles.ellipsis}>...</span>
                            )}
                          </React.Fragment>
                        );
                      })}
                  </div>
                  
                  <button 
                    className={`${styles.paginationButton} ${currentPage === totalPages || totalPages === 0 ? styles.disabled : ''}`}
                    onClick={nextPage}
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}
              
              <div className={styles.paginationInfo}>
                {filteredSeries?.length > 0 && (
                  <span>
                    Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredSeries.length)} of {filteredSeries.length} series
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <DeleteConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default SeriesAdmin;